import type { UploadConsultaResult } from "../types";
import { isNoConfiguredProviderResponse, NoConfiguredProviderError } from "@/lib/edgeFnError";

export interface AnalyzeTranscriptOptions {
  label: string;
  transcriptText: string;
  token: string;
  signal?: AbortSignal;
}

function createRequestId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getFunctionsBaseUrl(): string {
  const supabaseUrl =
    (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
  if (!supabaseUrl) throw new Error("VITE_SUPABASE_URL is not configured");
  return supabaseUrl.replace(/\/$/, "") + "/functions/v1/llm-proxy";
}

export const MIN_TRANSCRIPT_CHARS = 50;

export async function analyzeTranscript({
  label,
  transcriptText,
  token,
  signal,
}: AnalyzeTranscriptOptions): Promise<UploadConsultaResult> {
  const trimmed = transcriptText.trim();
  if (trimmed.length < MIN_TRANSCRIPT_CHARS) {
    throw new Error(
      `A transcrição precisa ter pelo menos ${MIN_TRANSCRIPT_CHARS} caracteres.`,
    );
  }

  const baseUrl = getFunctionsBaseUrl();
  const idempotencyKey = createRequestId();

  const resp = await fetch(`${baseUrl}/analyze-transcript`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      label: label.trim() || "Consulta",
      transcript_text: trimmed,
      idempotency_key: idempotencyKey,
    }),
    signal,
  });

  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({ error: resp.statusText }));
    if (isNoConfiguredProviderResponse(resp.status, errBody)) {
      throw new NoConfiguredProviderError(
        typeof errBody.detail === "string" ? errBody.detail : "no_configured_provider",
        { status: resp.status, code: typeof errBody.error === "string" ? errBody.error : undefined },
      );
    }
    if (errBody.error === "duplicate" && errBody.consulta_id) {
      return {
        jobId: errBody.job_id,
        consultaId: errBody.consulta_id,
        duplicated: true,
      };
    }
    if (errBody.error === "transcript_too_short") {
      throw new Error(
        typeof errBody.message === "string"
          ? errBody.message
          : `A transcrição precisa ter pelo menos ${MIN_TRANSCRIPT_CHARS} caracteres.`,
      );
    }
    if (errBody.error === "rate_limit_exceeded") {
      throw new Error("Limite de análises atingido. Tente novamente em uma hora.");
    }
    throw new Error(
      errBody.error
        ? `Erro do servidor: ${String(errBody.error)}`
        : `Erro HTTP ${resp.status}`,
    );
  }

  const data = (await resp.json()) as { job_id: string; consulta_id: string };
  return { jobId: data.job_id, consultaId: data.consulta_id };
}
